import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { partnershipAPI } from '../services/api';

// ── Public /partner-invite?token=... — invite acceptance ───────────────────────
// Not flag-gated (same as the backend routes it calls): an invited person
// must be able to see and accept their invite regardless of whether
// partnership_network has been switched on for general launch yet.
function fmtUGX(n) {
  return 'UGX ' + Math.round(n || 0).toLocaleString();
}

export default function PartnerInviteAccept() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) { setLoadError('This invite link is missing its token.'); setLoading(false); return; }
    partnershipAPI.getInvitePreview(token)
      .then(setPreview)
      .catch((err) => setLoadError(err.message || 'This invite link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setSubmitError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPassword) { setSubmitError('Passwords do not match.'); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      await partnershipAPI.acceptInvite(token, password);
      setAccepted(true);
    } catch (err) {
      setSubmitError(err.message || 'Failed to accept invite. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    page:  { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: 'var(--bg-page, #0a1628)' },
    hero:  { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', padding: '32px 20px', color: '#fff', textAlign: 'center' },
    title: { fontSize: 22, fontWeight: 800, margin: '0 0 8px' },
    sub:   { fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0 },
    body:  { padding: '24px 20px' },
    card:  { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, marginBottom: 20 },
    label: { fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4, marginTop: 14 },
    input: { width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
             border: '1px solid var(--border)', background: 'var(--bg-page)',
             color: 'var(--text-primary)', fontSize: 14 },
    btn:   { width: '100%', marginTop: 22, padding: '13px 0', borderRadius: 10,
             background: '#22d3ee', color: '#0f172a', fontWeight: 700, fontSize: 15,
             border: 'none', cursor: 'pointer' },
    btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    errBox: { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 14 },
    successBox: { background: 'var(--bg-card)', border: '2px solid #22d3ee', borderRadius: 14, padding: 20, textAlign: 'center' },
    successTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' },
    detailRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)' },
    loginLink: { display: 'inline-block', marginTop: 18, color: '#22d3ee', fontWeight: 700, textDecoration: 'none' },
  };

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <h1 style={styles.title}>You've Been Invited to Partner with DWI</h1>
        <p style={styles.sub}>Set a password to activate your partner account.</p>
      </div>

      <div style={styles.body}>
        {loading ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</p>
        ) : loadError ? (
          <div style={styles.card}>
            <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>This invite link isn't valid</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>{loadError}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '10px 0 0' }}>
              Ask DWI to resend your invite, or if you've already set your password, <Link to="/login" style={{ color: '#22d3ee' }}>log in here</Link>.
            </p>
          </div>
        ) : accepted ? (
          <div style={styles.successBox}>
            <p style={styles.successTitle}>You're all set, {preview?.partnerName}!</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              Your partner account is active. Log in to reach your partner portal.
            </p>
            <Link to="/login" style={styles.loginLink}>Go to Login →</Link>
          </div>
        ) : (
          <>
            <div style={styles.card}>
              <p style={{ fontSize: 14, color: 'var(--text-primary)', margin: '0 0 10px' }}>
                Hi <strong>{preview?.partnerName}</strong> — DWI has invited you to partner on a community internet site.
              </p>
              {preview?.site ? (
                <>
                  <div style={styles.detailRow}><span style={{ color: 'var(--text-muted)' }}>Site</span><strong>{preview.site.name}</strong></div>
                  {preview.site.project && (
                    <div style={styles.detailRow}><span style={{ color: 'var(--text-muted)' }}>Project</span><strong>{preview.site.project}</strong></div>
                  )}
                  <div style={styles.detailRow}><span style={{ color: 'var(--text-muted)' }}>Funding cost</span><strong>{fmtUGX(preview.site.fundingCost)}</strong></div>
                  <div style={styles.detailRow}><span style={{ color: 'var(--text-muted)' }}>Revenue share</span><strong>{preview.site.revenueSharePct}%{preview.site.shareTermMonths ? ` for ${preview.site.shareTermMonths} months` : ''}</strong></div>
                </>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  No site is attached to this invite yet — DWI will assign one once you're set up.
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <label style={styles.label}>Set a password *</label>
              <input style={styles.input} type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />

              <label style={styles.label}>Confirm password *</label>
              <input style={styles.input} type="password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} />

              {submitError && <div style={styles.errBox}>{submitError}</div>}

              <button type="submit" style={{ ...styles.btn, ...(submitting ? styles.btnDisabled : {}) }} disabled={submitting}>
                {submitting ? 'Activating…' : 'Activate My Partner Account'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
